import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { pdvApi } from '../lib/api';
import { useAuth } from './AuthContext';

/**
 * Context for the "current Unità Locale + Azienda" selected by the user.
 * v1.3.6 — esteso con currentAziendaId per supportare l'hard-gate del context switcher.
 * Persisted to sessionStorage so it survives navigation between pages.
 */
const CurrentPdvContext = createContext({
  currentPdvId: null,
  currentAziendaId: null,
  setCurrentPdvId: () => {},
  setCurrentAziendaId: () => {},
  clearCurrentPdv: () => {},
  needsContextSelection: () => false,
});

// v1.3.6 — Ruoli che vedono SOLO un perimetro chiuso → context auto-impostato
const SINGLE_SCOPE_ROLES = ['responsabile_ul', 'dipendente', 'collaboratore', 'gerente', 'azienda'];
// v1.3.8 — RIMOSSO hard-gate per admin/super_admin: hanno potere di vedere TUTTO senza obbligo.
// Solo ruoli che hanno multi-perimetro MA non sono admin globali sono soggetti al gate.
// In pratica DISABILITATO: nessuno è obbligato a scegliere. Le pagine che richiedono UL
// (Schede Prodotto, Libro Ingredienti) hanno il loro gate LOCALE come da v1.3.5.
const HARD_GATE_ROLES = []; // VUOTO: nessun ruolo ha hard-gate obbligatorio

export const CurrentPdvProvider = ({ children }) => {
  const { user } = useAuth();
  const [currentPdvId, setCurrentPdvIdState] = useState(() => {
    try { return sessionStorage.getItem('cen_current_pdv_id') || null; } catch { return null; }
  });
  const [currentAziendaId, setCurrentAziendaIdState] = useState(() => {
    try { return sessionStorage.getItem('cen_current_azienda_id') || null; } catch { return null; }
  });

  // Auto-select single PDV when user logs in and only has access to ONE UL
  // v1.3.6 — usa direttamente user.allowed_pdv_ids per ruoli single-scope (evita race con API)
  useEffect(() => {
    if (!user) return;
    if (currentPdvId) return;

    // Path 1 (PRIORITARIO): ruoli single-scope con allowed_pdv_ids esattamente 1 → set immediato
    if (SINGLE_SCOPE_ROLES.includes(user.role)) {
      const allowedPdv = user.allowed_pdv_ids || [];
      const allowedAz = user.allowed_azienda_ids || [];
      if (allowedPdv.length === 1) {
        const onlyPdv = allowedPdv[0];
        sessionStorage.setItem('cen_current_pdv_id', onlyPdv);
        setCurrentPdvIdState(onlyPdv);
        if (allowedAz.length >= 1) {
          sessionStorage.setItem('cen_current_azienda_id', allowedAz[0]);
          setCurrentAziendaIdState(allowedAz[0]);
        }
        return;
      }
    }

    // Path 2 (FALLBACK): chiamata API per scoprire se ho UN SOLO pdv
    let aborted = false;
    pdvApi.getAll()
      .then((r) => {
        if (aborted) return;
        const list = r.data || [];
        if (list.length === 1) {
          const onlyId = list[0].id;
          sessionStorage.setItem('cen_current_pdv_id', onlyId);
          setCurrentPdvIdState(onlyId);
          if (list[0].azienda_id) {
            sessionStorage.setItem('cen_current_azienda_id', list[0].azienda_id);
            setCurrentAziendaIdState(list[0].azienda_id);
          }
        }
      })
      .catch(() => {});
    return () => { aborted = true; };
  }, [user, currentPdvId]);

  // Sync across tabs
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === 'cen_current_pdv_id') setCurrentPdvIdState(e.newValue || null);
      if (e.key === 'cen_current_azienda_id') setCurrentAziendaIdState(e.newValue || null);
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const setCurrentPdvId = useCallback((id) => {
    if (id) {
      sessionStorage.setItem('cen_current_pdv_id', id);
      setCurrentPdvIdState(id);
    } else {
      sessionStorage.removeItem('cen_current_pdv_id');
      setCurrentPdvIdState(null);
    }
  }, []);

  const setCurrentAziendaId = useCallback((id) => {
    if (id) {
      sessionStorage.setItem('cen_current_azienda_id', id);
      setCurrentAziendaIdState(id);
    } else {
      sessionStorage.removeItem('cen_current_azienda_id');
      setCurrentAziendaIdState(null);
    }
    // Cambio azienda → reset PDV
    sessionStorage.removeItem('cen_current_pdv_id');
    setCurrentPdvIdState(null);
  }, []);

  const clearCurrentPdv = useCallback(() => {
    sessionStorage.removeItem('cen_current_pdv_id');
    sessionStorage.removeItem('cen_current_azienda_id');
    setCurrentPdvIdState(null);
    setCurrentAziendaIdState(null);
  }, []);

  // v1.3.8 — Hard-gate DISABILITATO: nessun ruolo è obbligato a scegliere contesto.
  // Admin/super_admin/consulente_haccp possono operare senza selezione (vedono tutto).
  // Il selettore globale rimane disponibile nella sidebar come scelta OPZIONALE.
  // Le pagine che richiedono UL hanno il loro gate locale (v1.3.5).
  const needsContextSelection = useCallback(() => false, []);

  return (
    <CurrentPdvContext.Provider value={{
      currentPdvId, currentAziendaId,
      setCurrentPdvId, setCurrentAziendaId,
      clearCurrentPdv, needsContextSelection,
    }}>
      {children}
    </CurrentPdvContext.Provider>
  );
};

export const useCurrentPdv = () => useContext(CurrentPdvContext);
